import { FrameMetrics, DetailedCollector } from './FPSMeasurement';

export interface OperationMetrics {
  operationId: string;
  operationName: string;
  startTime: number;
  endTime: number;
  totalDuration: number;
  frames: FrameMetrics[];
  avgFPS: number;
  minFPS: number;
  maxFPS: number;
  fps1stPercentile: number;
  fps5thPercentile: number;
  fps50thPercentile: number;
  fps95thPercentile: number;
  fps99thPercentile: number;
}

export interface BenchmarkConfig {
  rotationDuration: number;
  zoomDuration: number;
  rapidZoomSteps: number;
  rapidZoomStepDelay: number;
  panDuration: number;
  panDistance: number;
  selectedPlaceCount: number;
}

export interface BenchmarkResults {
  timestamp: Date;
  operations: OperationMetrics[];
  totalDuration: number;
  config: BenchmarkConfig;
}

export class MetricsCollector {
  private frameCollector: DetailedCollector = new DetailedCollector();
  private operations: Map<string, Partial<OperationMetrics>> = new Map();
  private currentOperationId: string | null = null;
  private operationStartTimestamp: number | null = null;
  private benchmarkStartTime: number = 0;
  private config: BenchmarkConfig;

  constructor(config: BenchmarkConfig) {
    this.config = config;
  }

  startBenchmark(): void {
    this.benchmarkStartTime = performance.now();
    this.operations.clear();
  }

  startOperation(operationId: string, operationName: string): void {
    this.currentOperationId = operationId;
    this.operationStartTimestamp = performance.now();

    this.operations.set(operationId, {
      operationId,
      operationName,
      startTime: this.operationStartTimestamp,
      frames: []
    });

    // Start collecting frames using DetailedCollector
    this.frameCollector.reset();
    this.frameCollector.start();
  }

  endOperation(operationId: string): void {
    if (this.currentOperationId !== operationId) {
      console.warn(`EndOperation called for ${operationId} but current operation is ${this.currentOperationId}`);
    }

    // Stop frame collection using DetailedCollector
    this.frameCollector.stop();

    const operation = this.operations.get(operationId);
    if (!operation || !operation.startTime) {
      console.error(`Operation ${operationId} not found or not started`);
      return;
    }

    const endTime = performance.now();
    const frames = this.frameCollector.getFrameMetrics();
    const fpsValues = frames.map(f => f.fps).filter(fps => fps > 0 && fps < 1000);

    if (fpsValues.length === 0) {
      console.warn(`No valid FPS values for operation ${operationId}`);
      return;
    }

    // Calculate statistics
    const avgFPS = fpsValues.reduce((sum, fps) => sum + fps, 0) / fpsValues.length;
    const minFPS = Math.min(...fpsValues);
    const maxFPS = Math.max(...fpsValues);

    // Update operation with complete metrics
    this.operations.set(operationId, {
      ...operation,
      endTime,
      totalDuration: endTime - operation.startTime,
      frames,
      avgFPS,
      minFPS,
      maxFPS,
      fps1stPercentile: this.calculatePercentile(fpsValues, 1),
      fps5thPercentile: this.calculatePercentile(fpsValues, 5),
      fps50thPercentile: this.calculatePercentile(fpsValues, 50),
      fps95thPercentile: this.calculatePercentile(fpsValues, 95),
      fps99thPercentile: this.calculatePercentile(fpsValues, 99),
    });

    this.currentOperationId = null;
    this.operationStartTimestamp = null;
  }

  getResults(): BenchmarkResults {
    const totalDuration = performance.now() - this.benchmarkStartTime;
    const operations: OperationMetrics[] = [];

    this.operations.forEach((op) => {
      if (this.isCompleteOperation(op)) {
        operations.push(op as OperationMetrics);
      }
    });

    return {
      timestamp: new Date(),
      operations,
      totalDuration,
      config: this.config
    };
  }

  private isCompleteOperation(op: Partial<OperationMetrics>): op is OperationMetrics {
    return (
      op.operationId !== undefined &&
      op.operationName !== undefined &&
      op.startTime !== undefined &&
      op.endTime !== undefined &&
      op.totalDuration !== undefined &&
      op.frames !== undefined &&
      op.avgFPS !== undefined &&
      op.minFPS !== undefined &&
      op.maxFPS !== undefined &&
      op.fps1stPercentile !== undefined &&
      op.fps5thPercentile !== undefined &&
      op.fps50thPercentile !== undefined &&
      op.fps95thPercentile !== undefined &&
      op.fps99thPercentile !== undefined
    );
  }

  private calculatePercentile(values: number[], percentile: number): number {
    if (values.length === 0) return 0;

    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.floor((percentile / 100) * sorted.length);
    const clampedIndex = Math.min(index, sorted.length - 1);

    return sorted[clampedIndex] || 0;
  }

  cleanup(): void {
    this.frameCollector.stop();
    this.operations.clear();
  }
}
